import { View } from 'react-native';
import { Avatar, Avatarable } from 'shared/components/avatar';
import { Text } from 'shared/components/text';
import { Props, Role, User } from 'shared/@types';
import { SvgImage } from 'shared/components/svg-image';
import { flags } from 'shared/library/i18n/flags';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { marketByCountryCode } from 'shared/library/i18n/market';
import { IdentityBadge } from 'shared/components/identity-badge';

export default memo(function UserCard({
  user,
  className,
  small,
  role = 'seller',
  showRole = true,
  showVerification = true,
  fullName = false,
  subtitle,
}: Props<{
  user: Partial<User>;
  role?: Role;
  showRole?: boolean;
  showVerification?: boolean;
  fullName?: boolean;
  small?: boolean;
  subtitle?: string;
}>) {
  const { t } = useTranslation('components');

  return (
    <View className={`flex-row items-center rounded-lg ${className}`}>
      <View className={'relative'}>
        <Avatar
          model={user as Avatarable}
          containerClassName={`${small ? 'mr-1.5 size-12' : 'mr-3 size-14'}`}
          imageClassName={small ? 'rounded-full object-cover' : 'rounded-lg object-cover'}
        />

        {user.country && (
          <View className={'absolute bottom-0 left-0'}>
            <SvgImage
              src={flags[marketByCountryCode(user.country!)?.language_code ?? 'sv']}
              containerClassName={'mr-1 aspect-[3/2.5] w-5 rounded-tr bg-white'}
            />
          </View>
        )}
      </View>

      <View className={'min-w-0 flex-1 items-start'}>
        <View className={'max-w-full flex-row items-center gap-1'}>
          <Text className={'min-w-0 shrink font-bold text-black'} numberOfLines={1}>
            {fullName ? `${user.first_name} ${user.last_name}` : user.display_name}
          </Text>

          {showVerification && (
            <IdentityBadge verifiedAt={user.verified_at} testID="user-card-verification" />
          )}
        </View>

        <Text className={'text-sm capitalize text-stone-600'} numberOfLines={1}>
          {subtitle ?? (
            <>
              {user.city ? user.city : ''}

              {showRole && (
                <>
                  {user.city && ' · '}
                  {role === 'buyer'
                    ? t('common:buyer')
                    : user.is_breeder || user.breeder_id
                      ? t('common:breeder')
                      : t('common:private_seller')}
                </>
              )}
            </>
          )}
        </Text>
      </View>
    </View>
  );
});
